import { type MapRef } from 'react-map-gl';
import turfDistance from '@turf/distance';
import { type BBox, lineString } from '@turf/helpers';
import bbox from '@turf/bbox';
import { LatLng, LngLat, Polylines } from '../types/Geo';
import { decodeNDim } from './legacyGeoJSONConversions';
import { defaultPadding } from './constants';

export const getTotalMetersFromPolylines = (polylines: Polylines) => {
  const filteredPolylines = polylines.filter(polyline => polyline.length > 0);
  if (filteredPolylines.length === 0) {
    return 0;
  }
  const points = filteredPolylines.reduce((allPoints, polyline) => {
    return allPoints.concat(decodeNDim(polyline).map(point => point.slice(0, 2) as LatLng));
  }, [] as LatLng[]);

  return points.reduce((meters, point, index) => {
    if (index === 0) {
      return 0;
    }
    // turfDistance wants points in the format of [lng, lat], so we need to switch the order of each point
    const prevPoint = [points[index - 1][1], points[index - 1][0]];
    const currPoint = [point[1], point[0]];
    return meters + turfDistance(prevPoint, currPoint, { units: 'meters' });
  }, 0);
};

export const getCompletedCoordinatesFromPolylines = (polylines: Polylines) => {
  const completedCoordinates = polylines.reduce((coordinates, line) => {
    const newCoordinates: LngLat[] = [];
    const decodedLine = decodeNDim(line);
    decodedLine.forEach(latLng => {
      newCoordinates.push([latLng[1], latLng[0]]);
    });
    return [...coordinates, ...newCoordinates];
  }, [] as LngLat[]);

  return completedCoordinates.length ? completedCoordinates : undefined;
};

const centerBoundingBoxAroundCoordinates = (boundingBox: BBox, coordinates: LngLat) => {
  // Calculate the dimensions of the original bounding box
  const lngDelta = boundingBox[2] - boundingBox[0];
  const latDelta = boundingBox[3] - boundingBox[1];

  // Calculate the center of the original bounding box
  const centerLng = boundingBox[0] + lngDelta / 2;
  const centerLat = boundingBox[1] + latDelta / 2;

  // Calculate the difference in longitude and latitude
  const lngDifference = Math.abs(coordinates[0] - centerLng);
  const latDifference = Math.abs(coordinates[1] - centerLat);

  // Extend the bounding box in all directions to keep the provided coordinates centered
  const newBoundingBox = [
    boundingBox[0] - lngDifference,
    boundingBox[1] - latDifference,
    boundingBox[2] + lngDifference,
    boundingBox[3] + latDifference
  ];

  return newBoundingBox;
};

export const orientMapAroundPolylines = (map: MapRef, polylines: Polylines, maxZoom?: number) => {
  const completedCoordinates = getCompletedCoordinatesFromPolylines(polylines);

  // 2+ points are required for lineString
  if (completedCoordinates && completedCoordinates.length >= 2) {
    const mapBBox = bbox(lineString(completedCoordinates));
    // Extend the bounding box to keep the last coordinates centered while showing the entire completed route
    const center = completedCoordinates[completedCoordinates.length - 1];
    const boundingBox = centerBoundingBoxAroundCoordinates(mapBBox, center);
    map.fitBounds(
      [
        [boundingBox[0], boundingBox[1]],
        [boundingBox[2], boundingBox[3]]
      ],
      { center, maxZoom, padding: defaultPadding }
    );
  }
};
